import React, { useState } from "react";
import JSZip from "jszip";
import FileUploadZone from "../components/uploads/FileUploadZone";
import UploadProgressDisplay from "../components/common/UploadProgressDisplay";
import apiClient from "../components/utils/apiClient";
import {
  ProjectInfoService,
  ProjectInfoFields,
} from "../components/services/ProjectInfoService";

const BulkUploadManager = ({ projectId, onClose, onBulkUpdate }) => {
  const [zipFile, setZipFile] = useState(null);
  const [zipFileNames, setZipFileNames] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [orderedFloors, setOrderedFloors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({});
  const [bulkUploadStatus, setBulkUploadStatus] = useState(null);

  const extractFloorNames = (filePaths) => {
    const uniqueFloorNames = new Set();

    filePaths.forEach((path) => {
      if (!path.includes("__MACOSX") && !path.includes(".DS_Store")) {
        const parts = path.split("/").filter((part) => part);
        if (parts.length >= 2) {
          const floorName = parts[parts.length - 2];
          uniqueFloorNames.add(floorName);
        }
      }
    });

    return Array.from(uniqueFloorNames);
  };

  const handleBulkFilesSelect = async (files) => {
    if (files.length === 0) return;

    const file = files[0];
    setZipFile(file);

    try {
      setBulkUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Reading ZIP contents...",
      }));

      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      const fileNames = Object.keys(contents.files);
      setZipFileNames(fileNames);

      const floorNames = extractFloorNames(fileNames);
      setFloorOptions([...new Set(floorNames)]);
      setOrderedFloors([]);

      setBulkUploadProgress((prev) => ({
        ...prev,
        [file.name]: "ZIP file read successfully",
      }));

      setTimeout(() => {
        setBulkUploadProgress({});
      }, 2000);
    } catch (error) {
      console.error("Error reading ZIP file:", error);
      setBulkUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Failed to read ZIP file",
      }));
      setBulkUploadStatus("error");
    }
  };

  const addToOrderedFloors = (floor) => {
    if (!orderedFloors.includes(floor)) {
      setOrderedFloors([...orderedFloors, floor]);
      setFloorOptions(floorOptions.filter((f) => f !== floor));
    }
  };

  const removeFromOrderedFloors = (floor) => {
    setOrderedFloors(orderedFloors.filter((f) => f !== floor));
    setFloorOptions([...floorOptions, floor]);
  };

  const categorizeFiles = (fileNames, orderedFloors) => {
    const facadeRegex = /(.+?)_facade.*\.glb/i;
    const floorRegex = /(.+?)_floors.*\.glb/i;
    const interiorRegex = /(.+?)_(interior|internal|inner).*\.glb/i;
    const pvRegex = /(.+?)_(pvs|PVs|pv).*\.glb/i;
    const stairsRegex = /(\d+\.\w+)?_stairs.*\.glb/i;
    const doorsRegex = /(\d+\.\w+)?_doors.*\.glb/i;

    const categorizedFiles = {
      facadeList: [],
      floorList: [],
      interiorList: [],
      pvPanelList: [],
      stairsList: [],
      doorsList: [],
    };

    const relevantFileNames = fileNames.filter(
      (fileName) =>
        !fileName.includes("__MACOSX") &&
        !fileName.includes(".DS_Store") &&
        fileName.endsWith(".glb")
    );

    orderedFloors.forEach((floor) => {
      relevantFileNames.forEach((fileName) => {
        const parts = fileName.split("/");
        const baseFileName = parts.pop();

        if (baseFileName.toLowerCase().includes(floor.toLowerCase())) {
          if (facadeRegex.test(baseFileName)) {
            categorizedFiles.facadeList.push(baseFileName.replace(".glb", ""));
          } else if (floorRegex.test(baseFileName)) {
            categorizedFiles.floorList.push(baseFileName.replace(".glb", ""));
          } else if (interiorRegex.test(baseFileName)) {
            categorizedFiles.interiorList.push(
              baseFileName.replace(".glb", "")
            );
          } else if (pvRegex.test(baseFileName)) {
            categorizedFiles.pvPanelList.push(baseFileName.replace(".glb", ""));
          } else if (doorsRegex.test(baseFileName)) {
            categorizedFiles.doorsList.push(baseFileName.replace(".glb", ""));
          } else if (stairsRegex.test(baseFileName)) {
            categorizedFiles.stairsList.push(baseFileName.replace(".glb", ""));
          }
        }
      });
    });

    return categorizedFiles;
  };

  const handleBulkUpload = async () => {
    if (!zipFile || orderedFloors.length === 0) {
      setBulkUploadStatus("error");
      return;
    }

    setIsSubmitting(true);
    try {
      setBulkUploadProgress((prev) => ({
        ...prev,
        [zipFile.name]: "Uploading ZIP file...",
      }));

      const uploadFormData = new FormData();
      uploadFormData.append("file", zipFile);

      const token = localStorage.getItem("token");
      const userID = localStorage.getItem("userID");

      await apiClient.post(
        `/project/setup/?project_id=${projectId}&user_id=${userID}&file_type=glb`,
        uploadFormData,
        {
          isFormData: true,
          showToast: false,
        }
      );

      setBulkUploadProgress((prev) => ({
        ...prev,
        [zipFile.name]: "Processing files...",
      }));

      const categorizedFiles = categorizeFiles(zipFileNames, orderedFloors);

      const apiRoot = process.env.REACT_APP_API_ROOT || "";
      const projectInfoService = new ProjectInfoService(apiRoot, token);

      const existingData = await apiClient.get("/api/project_info/", {
        params: { project_id: projectId },
        showToast: false,
      });

      const hasExistingRecord = existingData && existingData.length > 0;
      let recordId = hasExistingRecord ? existingData[0].id : null;

      await projectInfoService.updateListField(
        projectId,
        ProjectInfoFields.FLOOR_LIST,
        orderedFloors,
        { overwrite: true }
      );

      await projectInfoService.updateListField(
        projectId,
        ProjectInfoFields.CEILING_LIST,
        categorizedFiles.floorList,
        { overwrite: true }
      );

      await projectInfoService.updateListField(
        projectId,
        ProjectInfoFields.EXTERNAL_LIST,
        categorizedFiles.facadeList,
        { overwrite: true }
      );

      await projectInfoService.updateListField(
        projectId,
        ProjectInfoFields.INTERNAL_LIST,
        categorizedFiles.interiorList,
        { overwrite: true }
      );

      await projectInfoService.updateListField(
        projectId,
        "stairs",
        categorizedFiles.stairsList,
        { overwrite: true }
      );

      await projectInfoService.updateListField(
        projectId,
        "doors",
        categorizedFiles.doorsList,
        { overwrite: true }
      );

      if (categorizedFiles.pvPanelList.length > 0) {
        await projectInfoService.updateListField(
          projectId,
          "pv_panels",
          categorizedFiles.pvPanelList,
          { overwrite: true }
        );
      }

      setBulkUploadProgress((prev) => ({
        ...prev,
        [zipFile.name]: "Complete",
      }));

      setBulkUploadStatus("success");

      setTimeout(() => {
        onBulkUpdate();
      }, 1500);
    } catch (error) {
      console.error("Error:", error);
      setBulkUploadProgress((prev) => ({
        ...prev,
        [zipFile.name]: "Failed",
      }));
      setBulkUploadStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-6 p-6 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800">
          Replace All GLB Files
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          &times;
        </button>
      </div>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Upload ZIP File
        </h4>
        <FileUploadZone
          onFilesSelect={handleBulkFilesSelect}
          disabled={isSubmitting}
          accept=".zip"
          acceptedTypes={["ZIP"]}
        />
        <UploadProgressDisplay progress={bulkUploadProgress} />
      </div>

      {zipFileNames.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Floor Configuration
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Select the floors to include and arrange them in the desired order.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-2">
                Available Floors
              </h5>
              <select
                multiple
                size="8"
                className="w-full border border-gray-300 rounded-md shadow-sm
                  focus:ring-blue-500 focus:border-blue-500"
                onDoubleClick={(e) => {
                  if (e.target.value) addToOrderedFloors(e.target.value);
                }}
              >
                {floorOptions.map((floor, index) => (
                  <option key={index} value={floor}>
                    {floor}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex lg:flex-col justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const select = document.querySelector("select[multiple]");
                  if (select && select.value) addToOrderedFloors(select.value);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                  transition-colors duration-200"
              >
                Add →
              </button>
              <button
                type="button"
                onClick={() => {
                  const selects = document.querySelectorAll("select[multiple]");
                  if (selects.length > 1 && selects[1].value) {
                    removeFromOrderedFloors(selects[1].value);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                  transition-colors duration-200"
              >
                ← Remove
              </button>
            </div>

            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-2">
                Ordered Floors
              </h5>
              <select
                multiple
                size="8"
                className="w-full border border-gray-300 rounded-md shadow-sm
                  focus:ring-blue-500 focus:border-blue-500"
                onDoubleClick={(e) => {
                  if (e.target.value) removeFromOrderedFloors(e.target.value);
                }}
              >
                {orderedFloors.map((floor, index) => (
                  <option key={index} value={floor}>
                    {floor}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {bulkUploadStatus && (
        <div
          className={`p-4 mb-4 ${
            bulkUploadStatus === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          } border rounded-lg`}
        >
          <p className="text-sm text-center">
            {bulkUploadStatus === "success"
              ? "Scene configuration updated successfully!"
              : "Error updating scene configuration. Please try again."}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="mr-3 px-4 py-2 text-gray-700 border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={handleBulkUpload}
          disabled={!zipFile || isSubmitting || orderedFloors.length === 0}
          className={`px-4 py-2 rounded-md text-white ${
            !zipFile || isSubmitting || orderedFloors.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSubmitting ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
              Processing...
            </>
          ) : (
            "Update Scene Files"
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkUploadManager;
